<template>
  <div class="problem-analysis-phase d-flex flex-column">
    <v-row class="my-0">
      <v-col cols="12">
        <Subtitle
          class="phase-section__subtitle"
          :title="$t('problem_solving.phases.analyse')"
          :description="$t('problem_solving.analyse.analyse_description')"
        />
      </v-col>
      <v-col cols="12">
        <Alert
          :vuetifyProps="{
            dismissible: true,
            value: showAlert,
            color: 'blue',
            icon: 'mdi-tools',
          }"
          :text="
            $t(
              'problem_solving.analyse.gaps_conversion_banner_descriptionWithCount',
              {
                count: gaps.length,
              }
            )
          "
        />
      </v-col>
    </v-row>

    <v-row
      data-testid="problem-solving-4d"
      data-tourid="problem-solving-analyse-phase"
      class="my-0"
    >
      <v-col cols="5">
        <v-expansion-panels v-model="activeGapPanel" v-if="gaps.length > 0">
          <v-expansion-panel v-for="(gap, index) in gaps" :key="gap.id">
            <v-expansion-panel-header>
              <p class="body-2">
                {{ getGapTitle(index) }}
              </p>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="text-ui-700 font-normal">
              {{ gap.conclusion }}
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
      <v-col cols="7" v-if="isTranslating">
        <TranslationLoading />
      </v-col>
      <v-col cols="7" v-else>
        <RootCause
          v-for="(rootCause, index) in rootCauses"
          class="problem-analysis-phase__root-cause"
          data-testid="problem-solving-4d"
          :data-tourid="`problem-solving-root-cause-${index}`"
          :rootCause="rootCause"
          :title="
            $t('problem_solving.problem_analysis.root_cause_number', {
              count: index + 1,
            })
          "
          :isEditMode="isEditMode"
          :isErasable="rootCauses.length > 1"
          :key="index"
          :tid="`rootCause${index + 1}`"
          :markRequiredFields="markRequiredFields"
          @rootCauseChange="handleRootCauseChange($event, index)"
          @rootCauseDelete="handleDeleteRootCause(index)"
          @trackEvent="trackRevChange($event)"
        />
        <Button
          v-if="isEditMode && !hasReachedRootCausesLimit"
          data-testid="problem-solving-4d-add-button"
          data-tourid="problem-solving-root-cause-add-button"
          size="large"
          color="primary"
          plain
          :disabled="!canAddRootCauses"
          :text="$t('problem_solving.root_causes.add_root_cause')"
          @click="addRootCause"
        />
      </v-col>
    </v-row>

    <PhasesNavigateButtons
      :showNextButton="hasNextButton"
      :showBackButton="hasBackButton"
      @next="$emit('next')"
      @back="$emit('back')"
    />

    <DeleteConfirmationModal
      :show="showDeleteRootCause"
      :title="$t('problem_solving.root_causes.delete_root_cause_title')"
      :text="$t('problem_solving.root_causes.delete_root_cause_message')"
      @cancel="showDeleteRootCause = false"
      @delete="removeRootCause"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapMutations } from 'vuex'

import PhasesNavigateButtons from '@/components/Rev/PhasesNavigateButtons'
import Subtitle from '@/components/Rev/PhaseSection/Subtitle.vue'
import RootCause from '@/containers/ProblemSolvingView/RootCause.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import Alert from '@/components/Alert.vue'
import Button from '@/components/UI/Button'

import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import TranslationLoading from '@/components/Rev/TranslationLoading.vue'

const ROOT_CAUSE_TEMPLATE = {
  name: '',
  type: null,
  description: '',
}

export default {
  name: 'ProblemAnalysis',

  mixins: [trackerMixin],

  components: {
    Subtitle,
    RootCause,
    DeleteConfirmationModal,
    TranslationLoading,
    Alert,
    Button,
    PhasesNavigateButtons,
  },

  props: {
    isEditMode: {
      type: Boolean,
      default: false,
    },
    rev: {
      type: Object,
      required: true,
    },
    isTranslating: Boolean,
    hasNextButton: Boolean,
    hasBackButton: Boolean,
    markRequiredFields: Boolean,
  },

  data() {
    return {
      activeGapPanel: 0,
      currentRootCauseIndex: null,
      showDeleteRootCause: false,
    }
  },

  computed: {
    showAlert() {
      if (this.gaps.length < 1 || this.hasReachedRootCausesLimit) return false
      if (this.isEditMode) {
        this.trackGapsAlert('Show')
      }
      return this.isEditMode
    },

    gaps() {
      return this.rev?.processGaps ?? []
    },
    rootCauses() {
      const rootCauses = this.rev?.rootCauses

      return _.isEmpty(rootCauses)
        ? [{ ...ROOT_CAUSE_TEMPLATE, position: 0 }]
        : _.sortBy(rootCauses, 'position')
    },

    rootCauseImage() {
      return this.rev?.problemAnalysis?.rootCauseMedia?.[0] ?? null
    },

    hasReachedRootCausesLimit() {
      return (
        this.rootCauses.length >= this.$constants.problemSolving.maxRootCauses
      )
    },

    canAddRootCauses() {
      const lastRootCause = _.last(this.rootCauses)
      return Boolean(lastRootCause?.name?.trim())
    },

    allRootCausesAreFilled() {
      const filledRootCauses = this.rootCauses.filter(r => !!r?.name?.trim())
      return filledRootCauses.length === this.rootCauses.length
    },
  },

  methods: {
    ...mapMutations({
      updateRootCauseMedia: 'problemSolving/updateRootCauseMedia',
      updateRootCauses: 'problemSolving/updateRootCauses',
    }),

    trackCloseAlert() {
      this.trackGapsAlert('Close')
    },

    addRootCause() {
      this.$emit('revChange')
      this.updateRootCauses([
        ..._.cloneDeep(this.rootCauses),
        { ...ROOT_CAUSE_TEMPLATE },
      ])
    },

    handleDeleteRootCause(index) {
      this.currentRootCauseIndex = index
      const rootCause = this.rootCauses[index]
      const hasAnyData = Boolean(
        rootCause?.name?.trim?.() ||
          rootCause?.description?.trim?.() ||
          rootCause?.type?.trim?.()
      )

      if (hasAnyData) return this.showDeleteRootCauseConfirmation(index)
      return this.removeRootCause()
    },

    showDeleteRootCauseConfirmation(index) {
      this.currentRootCauseIndex = index
      this.showDeleteRootCause = true
    },

    removeRootCause() {
      this.$emit('revChange')
      const updatedRootCauses = _.cloneDeep(this.rootCauses)
      updatedRootCauses.splice(this.currentRootCauseIndex, 1)

      this.updateRootCauses(updatedRootCauses)

      this.currentRootCauseIndex = null
      this.showDeleteRootCause = false
    },

    handleRootCauseChange(rootCause, index) {
      this.$emit('revChange')
      const updatedRootCauses = _.cloneDeep(this.rootCauses)
      updatedRootCauses[index] = rootCause

      this.updateRootCauses(updatedRootCauses)
    },

    getGapTitle(index) {
      return this.$t('problem_solving.gaps_analysis_tool.gap_title', {
        gapNumber: (index + 1).toString().padStart(2, '0'),
      })
    },

    trackGapsAlert(action) {
      this.trackAlert(action, 'info', 'problem-solving-analyze')
    },

    trackRevChange(changedField) {
      this.trackUpdate(this.rev, changedField)
    },
  },
}
</script>

<style lang="scss" scoped>
.problem-analysis-phase {
  &__root-cause {
    &:last-child {
      margin-bottom: 0;
    }
  }
  .disabled {
    pointer-events: none;
    cursor: default;
    opacity: 1;
  }
}
</style>
